import { onScopeDispose } from 'vue'
import { useIntersectionObserver } from '@vueuse/core'
import { useGoogleAnalyticsStore } from '@/stores/GoogleAnalyticsStore'

const IMPRESSION_MS = 1000;
const ENGAGED_VIEW_MS = 10000;
const VIEWABILITY_RATIO = 0.5;

// Sends Complete_Report_Container_Impression after the target has been viewable for
// IMPRESSION_MS continuously, and Complete_Report_Container_Engaged_View once it has
// been viewable for ENGAGED_VIEW_MS cumulatively. Each fires at most once per instance.
export const useContainerViewTracking = (target, getEventParams) => {
  const googleAnalyticsStore = useGoogleAnalyticsStore();

  let elementViewable = false;
  let viewStart = null;
  let accumulatedMs = 0;
  let impressionTimeout = null;
  let engagedTimeout = null;
  let impressionSent = false;
  let engagedSent = false;

  // Viewable when at least half of the container is on screen, or, for containers
  // taller than the viewport, when it covers at least half of the viewport.
  const isViewable = (entry) => {
    if (!entry.isIntersecting) return false;

    if (entry.intersectionRatio >= VIEWABILITY_RATIO) return true;

    return !!entry.rootBounds?.height && entry.intersectionRect.height >= entry.rootBounds.height * VIEWABILITY_RATIO;
  }

  const sendImpression = () => {
    impressionSent = true;

    googleAnalyticsStore.sendEvent('Complete_Report_Container_Impression', getEventParams());
  }

  const sendEngagedView = () => {
    engagedSent = true;

    googleAnalyticsStore.sendEvent('Complete_Report_Container_Engaged_View', getEventParams());

    teardown();
  }

  const startTiming = () => {
    if (viewStart !== null || engagedSent) return;

    viewStart = performance.now();

    // The impression requires continuous viewability, so its timer restarts on each entry
    if (!impressionSent) impressionTimeout = setTimeout(sendImpression, IMPRESSION_MS);

    engagedTimeout = setTimeout(sendEngagedView, ENGAGED_VIEW_MS - accumulatedMs);
  }

  const pauseTiming = () => {
    if (viewStart === null) return;

    accumulatedMs += performance.now() - viewStart;
    viewStart = null;

    clearTimeout(impressionTimeout);
    clearTimeout(engagedTimeout);
  }

  const updateTiming = () => {
    if (elementViewable && document.visibilityState === 'visible') {
      startTiming();

      return;
    }

    pauseTiming();
  }

  const { stop } = useIntersectionObserver(
    target,
    (entries) => {
      elementViewable = isViewable(entries[entries.length - 1]);

      updateTiming();
    },
    { threshold: Array.from({ length: 21 }, (_, i) => i / 20) }
  );

  const teardown = () => {
    pauseTiming();
    stop();

    document.removeEventListener('visibilitychange', updateTiming);
  }

  document.addEventListener('visibilitychange', updateTiming);

  onScopeDispose(teardown);
}
